import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress
} from '@mui/material';
import { useSnackbar } from 'components/snackbar';
import { getCandidateStatusSummary } from 'api/admin';

/**
 * Component to display candidate status summary statistics
 *
 * This component fetches and displays a summary of candidates grouped by
 * their job search status along with the recommended communication frequency.
 *
 * @returns {JSX.Element}
 */
const RecommendationJobStatsPage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchCandidateStatusSummary();
    }, []);

    /**
     * Fetches the candidate status summary data from the API
     * and updates the component state. Displays error messages
     * if the request fails.
     * @returns {Promise<void>}
     */
    const fetchCandidateStatusSummary = async () => {
        try {
            setLoading(true);
            const response = await getCandidateStatusSummary();
            if (response && response?.success && response?.data) {
                setData(response.data);
            } else {
                enqueueSnackbar(response?.message || 'Failed to fetch data', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error fetching candidate status summary:', error);
            enqueueSnackbar('Error fetching candidate status summary', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box style={{ marginTop: '80px' }}>
            <Container>
                <Typography variant="h4" component="div" sx={{ marginBottom: '2rem' }}>
                    Recommendation Job Stats
                </Typography>

                <Paper elevation={3}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Job Search Status</strong></TableCell>
                                    <TableCell><strong>Candidate Count</strong></TableCell>
                                    <TableCell><strong>Communication Frequency (Days)</strong></TableCell>
                                    <TableCell><strong>Last Sent (Date)</strong></TableCell>
                                    <TableCell><strong>Next Schedule (Date)</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : data.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    data.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{row.job_search_status || 'N/A'}</TableCell>
                                            <TableCell>{row.candidate_count || 0}</TableCell>
                                            <TableCell>{row.communication_frequency_days || 'N/A'}</TableCell>
                                            <TableCell>{row.last_send_date || 'N/A'}</TableCell>
                                            <TableCell>{row.next_send_date || 'N/A'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Container>
        </Box>
    );
};

export default RecommendationJobStatsPage;
